import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { useContent } from "@/lib/content";
import { Button } from "@/components/ui/button";
import { History, Undo2, ChevronDown, ChevronUp } from "lucide-react";

const PAGE_NAMES = {
  home: "Home", hero_image: "Home", about: "About", gallery: "Gallery", etiquette: "Booking Etiquette", inquiry: "Booking Inquiry",
  tour: "Tour Schedule", travel: "Exclusive Travel", gifts: "Spoil Me", faq: "FAQ", blog: "Blog", contact: "Contact", links: "Links", footer: "Footer",
};

const pageOf = (key) => PAGE_NAMES[key.split(".")[0]] || "Site";

const describe = (value) => {
  if (!value) return "default";
  if (value.metadata?.hidden !== undefined) return value.metadata.hidden ? "hidden" : "visible";
  if (value.metadata?.items) return `${value.metadata.items.length} items`;
  if (value.content === "" || value.content == null) return "default";
  return value.content.length > 70 ? `“${value.content.slice(0, 70)}…”` : `“${value.content}”`;
};

const Entry = ({ entry, onRevert }) => {
  const [open, setOpen] = useState(false);
  const keys = Object.keys(entry.changes);
  const pages = [...new Set(keys.map(pageOf))];
  const when = new Date(entry.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  const isRevert = entry.source?.startsWith("revert");

  return (
    <div className="glass rounded-xl p-5" data-testid={`history-entry-${entry.id}`}>
      <div className="flex flex-wrap items-center gap-4 justify-between">
        <div>
          <p className="font-heading text-lg text-charcoal">
            {isRevert ? "Reverted " : "Published "}{entry.count} change{entry.count === 1 ? "" : "s"}
            <span className="font-body text-xs text-taupe ml-3">{pages.join(", ")}</span>
          </p>
          <p className="font-body text-xs text-stone-grey mt-1">{when}</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => setOpen(!open)} data-testid={`history-toggle-${entry.id}`}>
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />} Details
          </Button>
          <Button size="sm" variant="outline" onClick={() => onRevert(entry)} data-testid={`history-revert-${entry.id}`}>
            <Undo2 className="w-4 h-4 mr-2" /> Revert
          </Button>
        </div>
      </div>
      {open && (
        <ul className="mt-4 space-y-2 border-t border-sand pt-4">
          {keys.map((key) => (
            <li key={key} className="font-body text-sm">
              <span className="text-taupe text-xs tracking-wide">{pageOf(key)} · {key}</span>
              <div className="text-stone-grey"><span className="line-through opacity-60">{describe(entry.changes[key].before)}</span> → <span className="text-charcoal">{describe(entry.changes[key].after)}</span></div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const EditHistory = () => {
  const { token } = useAuth();
  const { refresh } = useContent();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/content/history`, { headers: { Authorization: `Bearer ${token}` } });
      setEntries(res.data);
    } catch (error) {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const revert = async (entry) => {
    const when = new Date(entry.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    if (!window.confirm(`Undo the ${entry.count} change(s) published on ${when}? The affected text, photos and sections will go back to how they were just before that publish.`)) return;
    try {
      const res = await axios.post(`${API}/admin/content/history/${entry.id}/revert`, {}, { headers });
      toast.success(`Reverted ${res.data.count} change(s)`);
      refresh();
      load();
    } catch (error) {
      toast.error("Failed to revert");
    }
  };

  return (
    <div data-testid="admin-edit-history">
      <h1 className="font-heading text-3xl text-charcoal mb-2">Edit History</h1>
      <p className="font-body text-stone-grey mb-8">Every publish is recorded. Revert any one with a single click — reverts are recorded too, so nothing is ever lost.</p>
      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : entries.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center">
          <History className="w-12 h-12 text-taupe mx-auto mb-4" />
          <p className="font-body text-stone-grey">No published changes yet.</p>
        </div>
      ) : (
        <div className="space-y-4">{entries.map((e) => <Entry key={e.id} entry={e} onRevert={revert} />)}</div>
      )}
    </div>
  );
};
